import { useState, useEffect } from 'react';
import { Users, Shield, Star, ChevronDown } from 'lucide-react';
import { useBackgrounds } from '../context/BackgroundContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Default members for display
const defaultMembers = [
    { id: '1', callsign: 'Wolfheart', rank: 'Admiral', division: 'Command', role: 'Fleet Commander', join_date: '2951-03-15', status: 'active' },
    { id: '2', callsign: 'Nova', rank: 'Captain', division: 'Strike', role: 'Squadron Leader', join_date: '2951-06-22', status: 'active' },
    { id: '3', callsign: 'Steele', rank: 'Commander', division: 'Heavy Assault', role: 'Tactical Officer', join_date: '2952-01-10', status: 'active' },
    { id: '4', callsign: 'Shadow', rank: 'Lieutenant', division: 'Recon', role: 'Intel Specialist', join_date: '2952-08-05', status: 'active' },
    { id: '5', callsign: 'Vex', rank: 'Lieutenant', division: 'Logistics', role: 'Supply Officer', join_date: '2953-02-18', status: 'active' },
    { id: '6', callsign: 'Fang', rank: 'Ensign', division: 'Strike', role: 'Pilot', join_date: '2953-07-30', status: 'active' },
    { id: '7', callsign: 'Razor', rank: 'Ensign', division: 'Marines', role: 'Ground Ops', join_date: '2953-09-12', status: 'active' },
    { id: '8', callsign: 'Ghost', rank: 'Ensign', division: 'Recon', role: 'Scout Pilot', join_date: '2954-01-05', status: 'active' },
];

export const Personnel = () => {
    const { getBackground } = useBackgrounds();
    const [members, setMembers] = useState(defaultMembers);
    const [ranks, setRanks] = useState([]);
    const [filterDivision, setFilterDivision] = useState('all');
    
    const personnelBg = getBackground('personnel');


const rankColors = {
    'Fleet Admiral': 'text-red-600',
    'General': 'text-orange-400',
    'Commodore': 'text-orange-600',
    'Captain': 'text-amber-400',
    'Wing Commander': 'text-indigo-400',
    'Lieutenant': 'text-emerald-400',
    'Ensign': 'text-gray-400',
    'Cadet': 'text-gray-700'
};

const rankOrder = ['Fleet Admiral','General', 'Commodore', 'Captain', 'Wing Commander', 'Lieutenant', 'Ensign', 'Cadet'];

    useEffect(() => {
        fetchMembers();
    }, []);

    const fetchMembers = async () => {
        try {
            const response = await axios.get(`${API}/members`);
            if (response.data.length > 0) {
                setMembers(response.data);
            }
        } catch (error) {
            console.error('Failed to fetch members:', error);
        }
    };

    const divisions = ['all', ...new Set(members.map(m => m.division))];
    
    const filteredMembers = filterDivision === 'all' 
        ? members 
        : members.filter(m => m.division === filterDivision);

    const sortedMembers = filteredMembers;

    return (
        <div className="min-h-screen bg-[#050505] pt-16" data-testid="personnel-page">
            {/* Hero */}
            <section className="relative h-[40vh] flex items-center justify-center overflow-hidden">
                {personnelBg && (
                    <>
                        <div 
                            className="absolute inset-0 bg-cover bg-center"
                            style={{ backgroundImage: `url(${personnelBg})` }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-[#050505]" />
                    </>
                )}
                <div className="relative z-10 text-center px-4">
                    <span className="font-mono text-xs text-red-500 tracking-[0.3em]">CREW MANIFEST</span>
                    <h1 className="font-heading font-black text-4xl sm:text-5xl text-white uppercase mt-2">
                        Personnel Roster
                    </h1>
                    <p className="font-mono text-sm text-gray-400 mt-4">
                        {members.length} Active Members • The Pack Stands Ready
                    </p>
                </div>
            </section>

            {/* Rank Legend */}
            <section className="px-4 py-8 border-b border-white/10">
                <div className="max-w-6xl mx-auto">
                    <div className="flex flex-wrap items-center justify-center gap-6">
                        {rankOrder.map((rank) => (
                            <div key={rank} className="flex items-center gap-2">
                                <Star className={`w-4 h-4 ${rankColors[rank]}`} />
                                <span className={`font-mono text-xs uppercase tracking-wider ${rankColors[rank]}`}>
                                    {rank}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Filter & Table */}
            <section className="px-4 py-8">
                <div className="max-w-6xl mx-auto">
                    {/* Filter */}
                    <div className="flex items-center justify-between mb-8">
                        <h2 className="font-heading font-bold text-xl text-white uppercase flex items-center gap-3">
                            <Users className="w-5 h-5 text-red-500" />
                            Active Roster
                        </h2>
                        <div className="relative">
                            <select
                                value={filterDivision}
                                onChange={(e) => setFilterDivision(e.target.value)}
                                className="appearance-none bg-black/50 border border-white/10 text-white font-mono text-sm px-4 py-2 pr-10 focus:outline-none focus:border-red-500/50"
                                data-testid="division-filter"
                            >
                                {divisions.map((div) => (
                                    <option key={div} value={div}>
                                        {div === 'all' ? 'All Divisions' : div}
                                    </option>
                                ))}
                            </select>
                            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
                        </div>
                    </div>

                    {/* Table */}
                    <div className="overflow-x-auto">
                        <table className="data-table" data-testid="personnel-table">
                            <thead>
                                <tr>
                                    <th>Rank</th>
                                    <th>Callsign</th>
                                    <th>Division</th>
                                    <th>Role</th>
                                    <th>Enlisted</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedMembers.map((member) => (
                                    <tr key={member.id} data-testid={`member-row-${member.id}`}>
                                        <td>
                                            <span className={`font-mono ${rankColors[member.rank]}`}>
                                                {member.rank}
                                            </span>
                                        </td>
                                        <td className="font-heading font-bold text-white">
                                            {member.callsign}
                                        </td>
                                        <td className="text-gray-400">{member.division}</td>
                                        <td className="text-gray-400">{member.role}</td>
                                        <td className="font-mono text-xs text-gray-500">{member.join_date}</td>
                                        <td>
                                            <span className={`badge ${member.status === 'active' ? 'badge-active' : 'badge-pending'}`}>
                                                {member.status}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Division Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12">
                        {divisions.filter(d => d !== 'all').map((division) => {
                            const count = members.filter(m => m.division === division).length;
                            return (
                                <div key={division} className="tactical-panel p-4 text-center">
                                    <div className="font-mono text-2xl font-bold text-white">{count}</div>
                                    <div className="font-mono text-xs text-gray-500 tracking-wider mt-1 uppercase">
                                        {division}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>
        </div>
    );
};
